import { z } from 'zod';
import { boundedSchema, digest, id, text } from '../models/common.mjs';

// Matches DATA's PostgreSQL integer CAS domain; clocks are never CAS tokens.
export const draftGenerationSchema = z.number().int().min(0).max(2147483647);
export const privateDraftContextSchema = z
  .object({
    companyId: id,
    actorId: id,
    origin: z.literal('native'),
    payer: z.enum(['poneva', 'user']),
  })
  .passthrough();
export const saveWorkingDraftInputSchema = z
  .object({
    draftId: id,
    expectedDraftGeneration: draftGenerationSchema.max(2147483646),
    operationId: id,
    inputDigest: digest,
    payloadRef: id,
    payloadDigest: digest,
  })
  .strict();
export const currentWorkingDraftRefSchema = z
  .object({
    draftId: id,
    draftGeneration: draftGenerationSchema,
    payloadRef: id.nullable(),
    payloadDigest: digest.nullable(),
  })
  .strict()
  .refine((v) => (v.payloadRef === null) === (v.payloadDigest === null));
export const savedWorkingDraftSchema = z
  .object({
    draftId: id,
    draftGeneration: draftGenerationSchema,
    payloadRef: id,
    payloadDigest: digest,
    operationReceiptRef: id,
    replayed: z.boolean(),
  })
  .strict();
export const saveWorkingDraftResultSchema = boundedSchema(
  z.union([
    z.object({ status: z.literal('saved'), value: savedWorkingDraftSchema }).strict(),
    z
      .object({
        status: z.literal('conflict'),
        code: z.literal('draft_generation_conflict'),
        current: currentWorkingDraftRefSchema,
      })
      .strict(),
    z
      .object({ status: z.literal('conflict'), code: z.literal('operation_digest_mismatch') })
      .strict(),
    z
      .object({
        status: z.literal('blocked'),
        code: z.enum([
          'not_authorized',
          'draft_not_open',
          'invalid_payload',
          'objects_unverified',
          'dependency_unavailable',
          'receipt_reconciliation_required',
        ]),
      })
      .strict(),
  ]),
);
export const lookupSaveReceiptResultSchema = boundedSchema(
  z.union([
    z.object({ status: z.literal('committed'), value: savedWorkingDraftSchema }).strict(),
    z.object({ status: z.enum(['absent', 'unresolved']) }).strict(),
    z
      .object({ status: z.literal('conflict'), code: z.literal('operation_digest_mismatch') })
      .strict(),
  ]),
);
export const interviewQuestionSchema = z.discriminatedUnion('kind', [
  z
    .object({
      id,
      labelKey: id,
      required: z.boolean(),
      kind: z.literal('text'),
      maxLength: z.number().int().min(1).max(32000),
    })
    .strict(),
  z
    .object({
      id,
      labelKey: id,
      required: z.boolean(),
      kind: z.literal('choice'),
      choices: z
        .array(z.object({ id, labelKey: id }).strict())
        .min(1)
        .max(100),
    })
    .strict(),
  z.object({ id, labelKey: id, required: z.boolean(), kind: z.literal('date-range') }).strict(),
  z
    .object({
      id,
      labelKey: id,
      required: z.boolean(),
      kind: z.literal('source'),
      requiredReadCapability: id,
      sourceIds: z.array(id).max(100).optional(),
    })
    .strict(),
]);
export const starterDeclarationSchema = z
  .object({
    id,
    revision: id,
    digest,
    titleKey: id,
    questions: z.array(interviewQuestionSchema).max(100),
    authoringRecipeRef: id,
    sources: z
      .array(z.object({ id, requiredReadCapability: id }).strict())
      .max(100)
      .optional(),
  })
  .strict();
export const artifactKindSchema = z.enum([
  'report',
  'deck',
  'document',
  'dashboard',
  'table',
  'chart',
  'spreadsheet',
  'page',
  'site',
]);
export const artifactStarterSchema = boundedSchema(
  starterDeclarationSchema.extend({ kind: artifactKindSchema }),
);
export class AuthoringError extends Error {
  constructor(code) {
    super(code);
    this.name = 'AuthoringError';
    this.code = code;
  }
}
export const draftTitleSchema = text(200).min(1);
